import React from 'react';

import clsx from 'clsx';

import { useLocalizedStringFormatter } from '@coursera/cds-common';

import i18nMessages from '@core/loaders/i18n';

import getLoadingIndicatorCss, { classes } from './getLoadingIndicatorCss';

export type Props = {
  /**
   * Invert the color scheme. Use when displaying over dark backgrounds.
   * @default false
   */
  inverted?: boolean;
  /**
   * When true, suppresses `role="status"` and `aria-label` so the element is
   * fully hidden from assistive technology. Use when embedding LoadingIndicator
   * inside a parent that already provides its own accessible status region.
   * @default false
   */
  decorative?: boolean;
} & React.ComponentPropsWithRef<'div'>;

/**
 * LoadingIndicator renders a standalone indeterminate loading ring with no visible label.
 * Use when you need only the spinning indicator without accompanying text.
 *
 * For loading states with a visible label, use `LoadingItem` instead.
 *
 * See [Props](__storybookUrl__/components-feedback-loadingindicator--default#props)
 */
const LoadingIndicator = React.forwardRef(function LoadingIndicator(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  const {
    inverted,
    decorative = false,
    className,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const label = decorative
    ? undefined
    : 'aria-label' in props
    ? ariaLabel
    : stringFormatter.format('loading');

  return (
    <div
      ref={ref}
      {...rest}
      aria-hidden={decorative || undefined}
      aria-label={label}
      className={clsx(className, {
        [classes.invert]: inverted,
      })}
      css={getLoadingIndicatorCss()}
      role={decorative ? undefined : 'status'}
    >
      <svg aria-hidden className={classes.loading} viewBox="0 0 100 100">
        <circle className={classes.mainCircle} />
      </svg>
    </div>
  );
});

export default LoadingIndicator;
